/* eslint-disable import/no-unresolved */
import type { ReactNode } from "react";
import React, { useEffect, useRef, useState } from "react";
import type { ChakraProps } from "@chakra-ui/react";
import { Box } from "@chakra-ui/react";
import type { Swiper as SwiperType, SwiperOptions } from "swiper";
import SwiperCore, {
  Autoplay,
  Navigation,
  Pagination,
  Scrollbar
} from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/thumbs";
import { Swiper } from "swiper/react";
import { scrollBarCss } from "theme/scrollBarCss";
import { MainSwiper } from "../ProductGallery/SwiperStyles";
import { CarouselNav } from "./CarouselNav";
import { colours } from "@shared/ui.design-system";

SwiperCore.use([Autoplay, Navigation, Pagination, Scrollbar]);

type Breakpoint = {
  slidesPerView?: number;
  slidesPerGroup?: number;
  loop?: boolean;
  showNavigation?: boolean;
};

type Breakpoints = {
  [key: number]: Breakpoint & SwiperOptions;
};

export type Props = CarouselProps & {
  children: ReactNode[];
};

export type CarouselProps = {
  additionalClassName?: string;
  slidesPerView?: number;
  gap?: number;
  centeredSlides?: boolean;
  arrowsColor?: string;
  arrowsShadow?: boolean;
  showNavigation?: boolean;
  nextBtnStyles?: ChakraProps;
  prevBtnStyles?: ChakraProps;
  nextIconStyles?: ChakraProps;
  prevIconStyles?: ChakraProps;
  pagination?: boolean | Record<string, string | boolean>;
  containerProps?: ChakraProps;
  autoplay?: boolean | Record<string, string | boolean | number>;
  loop?: boolean;
  arrowsLocation?: "internal" | "external";
  scrollbar?: boolean | Record<string, string>;
  breakpoints?: Breakpoints;
  iconSize?: string;
  slideToFirstPosition?: boolean;
  nextBtnActions?: () => void;
  prevBtnActions?: () => void;
  shouldSlideToFirstPosition?: (state: boolean) => void;
};

export const HomeCarousel: React.FC<Props> = ({
  arrowsColor = colours.secondary.light,
  arrowsShadow = true,
  autoplay = false,
  centeredSlides = true,
  children,
  containerProps = {},
  gap = 0,
  loop = true,
  nextBtnStyles = undefined,
  pagination = { type: "custom" },
  prevBtnStyles = undefined,
  nextIconStyles = undefined,
  prevIconStyles = undefined,
  showNavigation = true,
  scrollbar = false,
  slidesPerView = 1,
  arrowsLocation = "internal",
  breakpoints = {},
  iconSize = "2rem",
  slideToFirstPosition = true,
  nextBtnActions,
  prevBtnActions,
  additionalClassName,
  shouldSlideToFirstPosition
}) => {
  const prevButtonRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const [swipe, setSwipe] = useState<SwiperType | null>(null);
  const isPrevNextArrowsVisible =
    showNavigation && children.length > slidesPerView;

  const nextSlide = () => {
    swipe?.slideNext();

    if (nextBtnActions) {
      nextBtnActions();
    }
  };

  useEffect(() => {
    if (swiperRef.current && slideToFirstPosition) {
      return () => {
        shouldSlideToFirstPosition?.(false);
      };
    }
  }, [children, slideToFirstPosition, shouldSlideToFirstPosition]);

  const prevSlide = () => {
    swipe?.slidePrev();

    if (prevBtnActions) {
      prevBtnActions();
    }
  };

  return (
    <>
      {isPrevNextArrowsVisible && arrowsLocation === "external" && (
        <CarouselNav
          prevButtonRef={prevButtonRef}
          nextButtonRef={nextButtonRef}
          nextSlide={nextSlide}
          prevSlide={prevSlide}
          nextBtnStyles={nextBtnStyles}
          prevBtnStyles={prevBtnStyles}
          nextIconStyles={nextIconStyles}
          prevIconStyles={prevIconStyles}
          arrowsColor={arrowsColor}
          arrowsShadow={arrowsShadow}
          iconSize={iconSize}
          additionalClassName={additionalClassName}
        />
      )}
      <Box
        position="relative"
        width="100%"
        height="auto"
        css={scrollBarCss}
        {...containerProps}
      >
        <MainSwiper className={additionalClassName}>
          <Swiper
            className={additionalClassName}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onInit={(swiper) => {
              if (
                typeof swiper.params.navigation !== "boolean" &&
                swiper.params.navigation
              ) {
                const { navigation } = swiper.params;
                navigation.prevEl = `.swiper-button-prev ${additionalClassName}`;
                navigation.nextEl = `.swiper-button-next ${additionalClassName}`;
              }
              swiper.navigation.update();
            }}
            navigation={{
              nextEl: `.swiper-button-next ${additionalClassName}`,
              prevEl: `.swiper-button-prev ${additionalClassName}`
            }}
            onBeforeInit={(swiper) => setSwipe(swiper)}
            centeredSlides={centeredSlides}
            centeredSlidesBounds
            slidesPerView={slidesPerView}
            spaceBetween={gap}
            pagination={pagination}
            loop={loop}
            autoplay={autoplay}
            breakpoints={breakpoints}
            scrollbar={scrollbar}
          >
            {children}
            {isPrevNextArrowsVisible && arrowsLocation === "internal" && (
              <CarouselNav
                prevButtonRef={prevButtonRef}
                nextButtonRef={nextButtonRef}
                nextSlide={nextSlide}
                prevSlide={prevSlide}
                nextBtnStyles={nextBtnStyles}
                prevBtnStyles={prevBtnStyles}
                arrowsColor={arrowsColor}
                arrowsShadow={arrowsShadow}
                iconSize={iconSize}
              />
            )}
          </Swiper>
        </MainSwiper>
      </Box>
    </>
  );
};
